import React, { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import {
    Box,
    Button,
    Container,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Typography,
    Alert,
    Divider,
} from "@mui/material"
import Grid from "@mui/material/Grid"
import { useTranslation } from "react-i18next"
import apiClient from "../services/apiClient"
import cashSessionService from "../services/cashSessionService"
import CashOpenCloseForm from "../components/CashOpenCloseForm"
import CashMovementForm from "../components/CashMovementForm"
import ZReportView from "../components/ZReportView"
import { useAuth } from "../services/AuthContext"
import { getErrorMessage, UnknownRecord } from "../models/api"
import {
    CashMovementPayload,
    CashSessionModel,
    CompanyModel,
    OutletModel,
    RegisterModel,
    ZReportModel,
} from "../models/pos"

function CashSessionPage() {
    const location = useLocation()
    const { user } = useAuth()
    const { t, i18n } = useTranslation()
    const locale = i18n.language || "fr"
    const navState = (location.state || {}) as {
        selectedCompanyId?: string
        selectedOutletId?: string
        selectedRegisterId?: string
    }
    const selectedCompanyId = navState.selectedCompanyId
    const selectedOutletId = navState.selectedOutletId
    const selectedRegisterId = navState.selectedRegisterId
    const isCashier =
        Array.isArray(user?.roles) && user!.roles.includes("cashier")

    const [companies, setCompanies] = useState<CompanyModel[]>([])
    const [outlets, setOutlets] = useState<OutletModel[]>([])
    const [registers, setRegisters] = useState<RegisterModel[]>([])

    const [companyId, setCompanyId] = useState("")
    const [outletId, setOutletId] = useState("")
    const [registerId, setRegisterId] = useState("")

    const [currentSession, setCurrentSession] =
        useState<CashSessionModel | null>(null)
    const [sessions, setSessions] = useState<CashSessionModel[]>([])

    const [showOpenForm, setShowOpenForm] = useState(false)
    const [showCloseForm, setShowCloseForm] = useState(false)
    const [showMovementForm, setShowMovementForm] = useState(false)
    const [showZReport, setShowZReport] = useState(false)
    const [zReport, setZReport] = useState<ZReportModel | null>(null)

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false

        async function loadCompanies() {
            try {
                const res = await apiClient.get("/api/companies")
                if (cancelled) return
                const list = res.companies || []
                setCompanies(list)
                if (list.length > 0) {
                    const defaultCompanyId =
                        selectedCompanyId &&
                        list.some(
                            (company: CompanyModel) =>
                                company.id === selectedCompanyId
                        )
                            ? selectedCompanyId
                            : list[0].id
                    setCompanyId(defaultCompanyId)
                }
            } catch (e: unknown) {
                if (!cancelled) setError(getErrorMessage(e))
            }
        }

        loadCompanies()
        return () => {
            cancelled = true
        }
    }, [selectedCompanyId])

    useEffect(() => {
        let cancelled = false

        async function loadOutlets() {
            if (!companyId) {
                setOutlets([])
                setOutletId("")
                return
            }

            try {
                const res = await apiClient.get(
                    `/api/outlets?company_id=${companyId}`
                )
                if (cancelled) return
                const list = res.outlets || []
                setOutlets(list)
                const defaultOutletId =
                    selectedOutletId &&
                    list.some(
                        (outlet: OutletModel) => outlet.id === selectedOutletId
                    )
                        ? selectedOutletId
                        : list[0]?.id || ""
                setOutletId(defaultOutletId)
            } catch (e: unknown) {
                if (!cancelled) setError(getErrorMessage(e))
            }
        }

        loadOutlets()
        return () => {
            cancelled = true
        }
    }, [companyId, selectedOutletId])

    useEffect(() => {
        let cancelled = false

        async function loadRegisters() {
            if (!outletId) {
                setRegisters([])
                setRegisterId("")
                return
            }

            try {
                const res = await apiClient.get(
                    `/api/registers?outlet_id=${outletId}`
                )
                if (cancelled) return
                const list = res.registers || []
                setRegisters(list)
                const defaultRegisterId =
                    selectedRegisterId &&
                    list.some(
                        (register: RegisterModel) =>
                            register.id === selectedRegisterId
                    )
                        ? selectedRegisterId
                        : list[0]?.id || ""
                setRegisterId(defaultRegisterId)
            } catch (e: unknown) {
                if (!cancelled) setError(getErrorMessage(e))
            }
        }

        loadRegisters()
        return () => {
            cancelled = true
        }
    }, [outletId, selectedRegisterId])

    useEffect(() => {
        if (!companyId || !registerId) {
            setCurrentSession(null)
            return
        }

        let cancelled = false

        async function loadCurrentSession() {
            try {
                const session = await cashSessionService.getOpenSession(
                    companyId,
                    registerId
                )
                if (!cancelled) {
                    setCurrentSession(session as CashSessionModel)
                }
            } catch (_e: unknown) {
                if (!cancelled) {
                    setCurrentSession(null)
                }
            }
        }

        loadCurrentSession()
        return () => {
            cancelled = true
        }
    }, [companyId, registerId])

    useEffect(() => {
        if (!companyId || !outletId) {
            setSessions([])
            return
        }

        let cancelled = false

        async function loadSessions() {
            try {
                const list = await cashSessionService.listSessions(
                    companyId,
                    outletId
                )
                if (!cancelled) {
                    setSessions(list as CashSessionModel[])
                }
            } catch (e) {
                if (!cancelled) {
                    setSessions([])
                }
            }
        }

        loadSessions()
        return () => {
            cancelled = true
        }
    }, [companyId, outletId])

    async function handleOpenSession(data: UnknownRecord) {
        setError(null)
        setLoading(true)
        try {
            const session = (await cashSessionService.openSession(
                companyId,
                data
            )) as CashSessionModel
            setCurrentSession(session)
            setShowOpenForm(false)
            setSessions((prev) => [session, ...prev])
        } catch (e: unknown) {
            setError(getErrorMessage(e))
        } finally {
            setLoading(false)
        }
    }

    async function handleCloseSession(data: UnknownRecord) {
        if (!currentSession?.id) {
            setError(t("cashSessionPage.errors.noActiveSession"))
            return
        }
        setError(null)
        setLoading(true)
        try {
            const response = await cashSessionService.closeSession(
                companyId,
                currentSession.id,
                data
            )
            const session = response?.session as CashSessionModel | undefined
            if (!session) {
                throw new Error("Invalid close session response")
            }
            setCurrentSession(null)
            setShowCloseForm(false)
            setSessions((prev) =>
                prev.map((s) => (s.id === session.id ? session : s))
            )
        } catch (e: unknown) {
            setError(getErrorMessage(e))
        } finally {
            setLoading(false)
        }
    }

    async function handleRecordMovement(data: CashMovementPayload) {
        setError(null)
        setLoading(true)
        try {
            await cashSessionService.recordCashMovement(companyId, data)
            setShowMovementForm(false)
        } catch (e: unknown) {
            setError(getErrorMessage(e))
        } finally {
            setLoading(false)
        }
    }

    async function handleViewZReport(sessionId: string) {
        setError(null)
        setLoading(true)
        try {
            const report = (await cashSessionService.getZReport(
                companyId,
                sessionId
            )) as ZReportModel
            setZReport(report)
            setShowZReport(true)
        } catch (e: unknown) {
            setError(getErrorMessage(e))
        } finally {
            setLoading(false)
        }
    }

    const canOperate = !!companyId && !!outletId && !!registerId
    const headerTitle = t("cashSessionPage.title")
    const companyLabel = t("cashSessionPage.filters.company")
    const outletLabel = t("cashSessionPage.filters.outlet")
    const registerLabel = t("cashSessionPage.filters.register")
    const currentSessionTitle = t("cashSessionPage.currentSession.title")
    const noSessionMessage = t("cashSessionPage.currentSession.empty")
    const openCashSessionLabel = t("cashSessionPage.currentSession.openButton")
    const sessionIdLabel = t("cashSessionPage.currentSession.sessionId")
    const openedAtLabel = t("cashSessionPage.currentSession.openedAt")
    const openingFloatLabel = t("cashSessionPage.currentSession.openingFloat")
    const recordMovementLabel = t(
        "cashSessionPage.currentSession.recordMovement"
    )
    const closeSessionLabel = t("cashSessionPage.currentSession.closeButton")
    const recentSessionsTitle = t("cashSessionPage.recentSessions.title")
    const noSessionsLabel = t("cashSessionPage.recentSessions.empty")
    const statusLabel = t("cashSessionPage.recentSessions.status")
    const openedLabel = t("cashSessionPage.recentSessions.opened")
    const expectedLabel = t("cashSessionPage.recentSessions.expected")
    const countedLabel = t("cashSessionPage.recentSessions.counted")
    const discrepancyLabel = t("cashSessionPage.recentSessions.discrepancy")
    const viewZReportLabel = t("cashSessionPage.recentSessions.viewZReport")

    return (
        <Container
            maxWidth={false}
            sx={{
                py: { xs: 1, sm: 2 },
                px: { xs: 1, sm: 2, md: 3 },
            }}
        >
            <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {headerTitle}
                </Typography>
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
                <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                            <InputLabel id="company-label">
                                {companyLabel}
                            </InputLabel>
                            <Select
                                labelId="company-label"
                                label={companyLabel}
                                value={companyId}
                                onChange={(e) => setCompanyId(e.target.value)}
                                disabled={isCashier}
                                sx={{ minHeight: 48 }}
                            >
                                {companies.map((c) => (
                                    <MenuItem
                                        key={c.id}
                                        value={c.id}
                                        sx={{ minHeight: 44 }}
                                    >
                                        {c.legal_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                            <InputLabel id="outlet-label">
                                {outletLabel}
                            </InputLabel>
                            <Select
                                labelId="outlet-label"
                                label={outletLabel}
                                value={outletId}
                                onChange={(e) => setOutletId(e.target.value)}
                                disabled={isCashier}
                                sx={{ minHeight: 48 }}
                            >
                                {outlets.map((o) => (
                                    <MenuItem
                                        key={o.id}
                                        value={o.id}
                                        sx={{ minHeight: 44 }}
                                    >
                                        {o.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel id="register-label">
                                {registerLabel}
                            </InputLabel>
                            <Select
                                labelId="register-label"
                                label={registerLabel}
                                value={registerId}
                                onChange={(e) => setRegisterId(e.target.value)}
                                disabled={isCashier}
                                sx={{ minHeight: 48 }}
                            >
                                {registers.map((r) => (
                                    <MenuItem
                                        key={r.id}
                                        value={r.id}
                                        sx={{ minHeight: 44 }}
                                    >
                                        {r.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {canOperate && (
                <>
                    <Paper
                        variant="outlined"
                        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}
                    >
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {currentSessionTitle}
                        </Typography>

                        {!currentSession && (
                            <Box>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 2 }}
                                >
                                    {noSessionMessage}
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={() => setShowOpenForm(true)}
                                    disabled={loading}
                                    sx={{ minHeight: 48 }}
                                >
                                    {openCashSessionLabel}
                                </Button>
                            </Box>
                        )}

                        {currentSession && (
                            <Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {sessionIdLabel}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontFamily: "monospace" }}
                                        >
                                            {currentSession.id}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {openedAtLabel}
                                        </Typography>
                                        <Typography variant="body1">
                                            {currentSession.opened_at
                                                ? new Date(
                                                      currentSession.opened_at
                                                  ).toLocaleString(locale)
                                                : "—"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {openingFloatLabel}
                                        </Typography>
                                        <Typography variant="body1">
                                            {(
                                                Number(
                                                    currentSession.opening_float_minor ||
                                                        0
                                                ) / 100
                                            ).toFixed(2)}{" "}
                                            {currentSession.currency}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2 }} />

                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 1,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={() =>
                                            setShowMovementForm(true)
                                        }
                                        disabled={loading}
                                        sx={{ minHeight: 48 }}
                                    >
                                        {recordMovementLabel}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={() => setShowCloseForm(true)}
                                        disabled={loading}
                                        sx={{ minHeight: 48 }}
                                    >
                                        {closeSessionLabel}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Paper>

                    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {recentSessionsTitle}
                        </Typography>
                        {sessions.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                {noSessionsLabel}
                            </Typography>
                        )}
                        {sessions.slice(0, 10).map((session) => (
                            <Box
                                key={session.id}
                                sx={{
                                    p: 2,
                                    mb: 1,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                }}
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {statusLabel}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 500 }}
                                        >
                                            {String(
                                                session.status || ""
                                            ).toUpperCase()}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {openedLabel}
                                        </Typography>
                                        <Typography variant="body1">
                                            {session.opened_at
                                                ? new Date(
                                                      session.opened_at
                                                  ).toLocaleDateString(locale)
                                                : "—"}
                                        </Typography>
                                    </Grid>
                                    {String(session.status) === "closed" && (
                                        <>
                                            <Grid item xs={6} sm={3}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {expectedLabel}
                                                </Typography>
                                                <Typography variant="body1">
                                                    {(
                                                        Number(
                                                            session.expected_cash_minor ||
                                                                0
                                                        ) / 100
                                                    ).toFixed(2)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6} sm={3}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {countedLabel}
                                                </Typography>
                                                <Typography variant="body1">
                                                    {(
                                                        Number(
                                                            session.counted_cash_minor ||
                                                                0
                                                        ) / 100
                                                    ).toFixed(2)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {discrepancyLabel}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    color={
                                                        Number(
                                                            session.discrepancy_minor ||
                                                                0
                                                        ) === 0
                                                            ? "success.main"
                                                            : "error.main"
                                                    }
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {(
                                                        Number(
                                                            session.discrepancy_minor ||
                                                                0
                                                        ) / 100
                                                    ).toFixed(2)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() =>
                                                        handleViewZReport(
                                                            session.id
                                                        )
                                                    }
                                                    disabled={loading}
                                                >
                                                    {viewZReportLabel}
                                                </Button>
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </Box>
                        ))}
                    </Paper>
                </>
            )}

            <CashOpenCloseForm
                open={showOpenForm}
                onClose={() => setShowOpenForm(false)}
                onSubmit={handleOpenSession}
                loading={loading}
                mode="open"
                outletId={outletId}
                registerId={registerId}
            />

            <CashOpenCloseForm
                open={showCloseForm}
                onClose={() => setShowCloseForm(false)}
                onSubmit={handleCloseSession}
                loading={loading}
                mode="close"
                session={currentSession}
            />

            <CashMovementForm
                open={showMovementForm}
                onClose={() => setShowMovementForm(false)}
                onSubmit={handleRecordMovement}
                loading={loading}
                outletId={outletId}
                registerId={registerId}
                cashSessionId={currentSession?.id}
            />

            <ZReportView
                open={showZReport}
                onClose={() => setShowZReport(false)}
                report={zReport}
            />
        </Container>
    )
}

export default CashSessionPage
